import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
  Alert,
  Autocomplete,
  CircularProgress,
  IconButton,
  useTheme
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  People as CustomerIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Home as HomeIcon,
  AccountBalance as AccountBalanceIcon,
  CreditCard as CreditIcon,
  Payment as PaymentIcon,
  Receipt as TaxIcon,
  Code as CodeIcon,
  Language as LanguageIcon
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { Customer, CreateCustomerDto, UpdateCustomerDto } from '../../types/customers.types';
import { customersService } from '../../services/customers.service';
import { chartOfAccountsService } from '../../services/chartOfAccounts.service';

interface CustomerDialogProps {
  open: boolean;
  onClose: () => void;
  customer?: Customer;
  onSave: () => void;
}

interface Account {
  accountId: number;
  accountCode: string;
  accountName: string;
  accountNameAr: string;
  accountType: string;
}

const CustomerDialog: React.FC<CustomerDialogProps> = ({ open, onClose, customer, onSave }) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const t = {
    addCustomer: isRTL ? 'إضافة عميل جديد' : 'Add New Customer',
    editCustomer: isRTL ? 'تعديل عميل' : 'Edit Customer',
    basicInfo: isRTL ? 'المعلومات الأساسية' : 'Basic Information',
    contactInfo: isRTL ? 'معلومات الاتصال' : 'Contact Information',
    financialInfo: isRTL ? 'المعلومات المالية' : 'Financial Information',
    accountingInfo: isRTL ? 'المعلومات المحاسبية' : 'Accounting Information',
    customerCode: isRTL ? 'كود العميل' : 'Customer Code',
    customerName: isRTL ? 'اسم العميل (EN)' : 'Customer Name (EN)',
    customerNameAr: isRTL ? 'اسم العميل (AR)' : 'Customer Name (AR)',
    taxNumber: isRTL ? 'الرقم الضريبي' : 'Tax Number',
    phone: isRTL ? 'الهاتف' : 'Phone',
    email: isRTL ? 'البريد الإلكتروني' : 'Email',
    address: isRTL ? 'العنوان' : 'Address',
    paymentTermDays: isRTL ? 'مدة الدفع (أيام)' : 'Payment Terms (Days)',
    creditLimit: isRTL ? 'الحد الائتماني' : 'Credit Limit',
    customerAccount: isRTL ? 'حساب العميل' : 'Customer Account',
    selectCustomerAccount: isRTL ? 'اختر حساب العميل' : 'Select Customer Account',
    isActive: isRTL ? 'نشط' : 'Active',
    save: isRTL ? 'حفظ' : 'Save',
    cancel: isRTL ? 'إلغاء' : 'Cancel',
    optional: isRTL ? 'اختياري' : 'Optional',
    required: isRTL ? 'مطلوب' : 'Required',
    loadingAccounts: isRTL ? 'جاري تحميل الحسابات...' : 'Loading accounts...',
    noAccountsFound: isRTL ? 'لا توجد حسابات نهائية' : 'No final accounts found'
  };

  const [formData, setFormData] = useState({
    customerCode: '',
    customerName: '',
    customerNameAr: '',
    taxNumber: '',
    address: '',
    phone: '',
    email: '',
    paymentTermDays: 30,
    creditLimit: 0,
    accountId: null as number | null,
    isActive: true,
  });

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      loadAccounts();
      if (customer) {
        setFormData({
          customerCode: customer.customerCode,
          customerName: customer.customerName,
          customerNameAr: customer.customerNameAr,
          taxNumber: customer.taxNumber || '',
          address: customer.address || '',
          phone: customer.phone || '',
          email: customer.email || '',
          paymentTermDays: customer.paymentTermDays || 30,
          creditLimit: customer.creditLimit || 0,
          accountId: customer.accountId || null,
          isActive: customer.isActive,
        });
      } else {
        loadNewCustomerCode();
      }
    }
  }, [open, customer]);

  const loadAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const data = await chartOfAccountsService.getAll();
      const finalAccounts = data.filter((acc: any) => 
        acc.isFinalAccount && acc.isActive
      );
      setAccounts(finalAccounts);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const loadNewCustomerCode = async () => {
    try {
      const code = await customersService.generateCode();
      setFormData(prev => ({ ...prev, customerCode: code }));
    } catch (error) {
      console.error('Failed to generate customer code:', error);
    }
  };

  const handleGenerateCode = () => {
    loadNewCustomerCode();
  };

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.customerName.trim()) {
      newErrors.customerName = isRTL ? 'اسم العميل (إنجليزي) مطلوب' : 'Customer name (EN) is required';
    }
    if (!formData.customerNameAr.trim()) {
      newErrors.customerNameAr = isRTL ? 'اسم العميل (عربي) مطلوب' : 'Customer name (AR) is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      if (customer) {
        const updateDto: UpdateCustomerDto = {
          customerId: customer.customerId,
          customerName: formData.customerName,
          customerNameAr: formData.customerNameAr,
          taxNumber: formData.taxNumber || undefined,
          address: formData.address || undefined,
          phone: formData.phone || undefined,
          email: formData.email || undefined,
          paymentTermDays: formData.paymentTermDays,
          creditLimit: formData.creditLimit || undefined,
          accountId: formData.accountId ?? undefined,
          isActive: formData.isActive,
        };
        await customersService.update(customer.customerId, updateDto);
      } else {
        const createDto: CreateCustomerDto = {
          customerCode: formData.customerCode,
          customerName: formData.customerName,
          customerNameAr: formData.customerNameAr,
          taxNumber: formData.taxNumber || undefined,
          address: formData.address || undefined,
          phone: formData.phone || undefined,
          email: formData.email || undefined,
          paymentTermDays: formData.paymentTermDays,
          creditLimit: formData.creditLimit || undefined,
          accountId: formData.accountId ?? undefined,
        };
        await customersService.create(createDto);
      }
      onSave();
      handleClose();
    } catch (error: any) {
      console.error('Failed to save customer:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      customerCode: '',
      customerName: '',
      customerNameAr: '',
      taxNumber: '',
      address: '',
      phone: '',
      email: '',
      paymentTermDays: 30,
      creditLimit: 0,
      accountId: null,
      isActive: true,
    });
    setErrors({});
    onClose();
  };

  const selectedAccount = accounts.find(acc => acc.accountId === formData.accountId);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth 
      dir={isRTL ? 'rtl' : 'ltr'}
      PaperProps={{ sx: { borderRadius: 3, bgcolor: isDarkMode ? '#1a1a2e' : 'white' } }}
    >
      <DialogTitle sx={{ background: isDarkMode ? 'linear-gradient(135deg, #0f3460 0%, #16213e 100%)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CustomerIcon />
          <Typography variant="h6" fontWeight="bold">{customer ? t.editCustomer : t.addCustomer}</Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <BusinessIcon sx={{ color: '#667eea' }} />
            <Typography variant="h6" fontWeight="bold" sx={{ color: '#667eea' }}>{t.basicInfo}</Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField fullWidth label={t.customerCode} value={formData.customerCode} onChange={handleChange('customerCode')} disabled={!!customer} InputProps={{ startAdornment: <CodeIcon sx={{ mr: 1, color: 'action.active' }} /> }} />
              {!customer && <IconButton color="primary" onClick={handleGenerateCode} sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)' }}><RefreshIcon /></IconButton>}
            </Box>
            <TextField fullWidth label={`${t.taxNumber} (${t.optional})`} value={formData.taxNumber} onChange={handleChange('taxNumber')} InputProps={{ startAdornment: <TaxIcon sx={{ mr: 1, color: 'action.active' }} /> }} />
            <TextField fullWidth label={`${t.customerName} *`} value={formData.customerName} onChange={handleChange('customerName')} error={!!errors.customerName} helperText={errors.customerName} required InputProps={{ startAdornment: <LanguageIcon sx={{ mr: 1, color: 'action.active' }} /> }} />
            <TextField fullWidth label={`${t.customerNameAr} *`} value={formData.customerNameAr} onChange={handleChange('customerNameAr')} error={!!errors.customerNameAr} helperText={errors.customerNameAr} required InputProps={{ startAdornment: <LanguageIcon sx={{ mr: 1, color: 'action.active' }} /> }} />
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PersonIcon sx={{ color: '#667eea' }} />
            <Typography variant="h6" fontWeight="bold" sx={{ color: '#667eea' }}>{t.contactInfo}</Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <TextField fullWidth label={`${t.phone} (${t.optional})`} value={formData.phone} onChange={handleChange('phone')} InputProps={{ startAdornment: <PhoneIcon sx={{ mr: 1, color: 'action.active' }} /> }} />
            <TextField fullWidth label={`${t.email} (${t.optional})`} type="email" value={formData.email} onChange={handleChange('email')} InputProps={{ startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} /> }} />
            <Box sx={{ gridColumn: 'span 2' }}>
              <TextField fullWidth label={`${t.address} (${t.optional})`} value={formData.address} onChange={handleChange('address')} multiline rows={2} InputProps={{ startAdornment: <HomeIcon sx={{ mr: 1, mt: 1, color: 'action.active' }} /> }} />
            </Box>
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CreditIcon sx={{ color: '#667eea' }} />
            <Typography variant="h6" fontWeight="bold" sx={{ color: '#667eea' }}>{t.financialInfo}</Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <TextField fullWidth label={`${t.creditLimit} (${t.optional})`} type="number" value={formData.creditLimit} onChange={handleChange('creditLimit')} inputProps={{ step: 0.001 }} InputProps={{ startAdornment: <CreditIcon sx={{ mr: 1, color: 'action.active' }} /> }} />
            <TextField fullWidth label={`${t.paymentTermDays} (${t.optional})`} type="number" value={formData.paymentTermDays} onChange={handleChange('paymentTermDays')} InputProps={{ startAdornment: <PaymentIcon sx={{ mr: 1, color: 'action.active' }} /> }} />
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AccountBalanceIcon sx={{ color: '#667eea' }} />
            <Typography variant="h6" fontWeight="bold" sx={{ color: '#667eea' }}>{t.accountingInfo}</Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Autocomplete fullWidth options={accounts} getOptionLabel={(option) => `${option.accountCode} - ${isRTL ? option.accountNameAr : option.accountName} (${option.accountType})`} value={selectedAccount || null} onChange={(_, newValue) => { setFormData({ ...formData, accountId: newValue?.accountId || null }); }} loading={loadingAccounts} renderInput={(params) => <TextField {...params} label={`${t.customerAccount} (${t.optional})`} placeholder={t.selectCustomerAccount} InputProps={{ ...params.InputProps, startAdornment: (<><AccountBalanceIcon sx={{ ml: 1, mr: 1, color: 'action.active' }} />{params.InputProps.startAdornment}</>), endAdornment: (<>{loadingAccounts ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</>), }} />} renderOption={(props, option) => { const { key, ...otherProps } = props; return (<li key={key} {...otherProps}><Box><Typography variant="body2" fontWeight="bold">{option.accountCode}</Typography><Typography variant="caption" color="text.secondary">{isRTL ? option.accountNameAr : option.accountName} • {option.accountType}</Typography></Box></li>); }} noOptionsText={t.noAccountsFound} />
          {!formData.accountId && <Alert severity="info" sx={{ mt: 2 }}>{isRTL ? 'ربط العميل بحساب محاسبي نهائي يساعد في تتبع المستحقات والمقبوضات بدقة' : 'Linking customer to a final account helps track receivables and collections accurately'}</Alert>}
        </Box>

        {customer && (
          <Box sx={{ mt: 3, p: 2, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5', borderRadius: 2 }}>
            <FormControlLabel control={<Switch checked={formData.isActive} onChange={handleChange('isActive')} color="success" />} label={<Typography variant="body1" fontWeight="bold">{t.isActive}</Typography>} />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
        <Button onClick={handleClose} disabled={loading} size="large">{t.cancel}</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading} startIcon={loading ? <CircularProgress size={20} /> : null} size="large" sx={{ background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)', boxShadow: '0 3px 5px 2px rgba(102, 126, 234, .3)', '&:hover': { background: 'linear-gradient(45deg, #5568d3 30%, #653d8a 90%)' } }}>{t.save}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerDialog;